import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, CircularProgress, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import ProductCard from "../Categories/ProductCard";
import { styled } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { motion } from "framer-motion";

const NavButton = styled(IconButton)(() => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 20,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  color: "#fff",
  width: 44,
  height: 44,
  borderRadius: "50%",
  boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.7)" },
}));

export default function TwoForOneProducts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/ofertas/2x1/");
        const data = await res.json();
        const formatted = (Array.isArray(data) ? data : []).map((p) => ({
          id: p.id,
          code: p.code,
          name: p.name,
          slug: p.slug,
          section: p.section,
          category: p.category,
          brand: p.brand,
          pvp1: p.pvp1,
          precio_descuento: p.precio_descuento ?? null,
          tipo_promocion: p.tipo_promocion,
          valor_promocion: p.valor_promocion ?? null,
          images: p.images || [],
          variants: p.variants || [],
        }));
        setItems(formatted);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!items.length) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
      <Box sx={{ maxWidth: 1200, mx: "auto", mt: 6, px: { xs: 1.5, sm: 2 }, position: "relative" }}>
        <Typography
          variant="h6"
          sx={{
            mb: { xs: 2, sm: 3 },
            color: "#003366",
            fontWeight: "bold",
            textAlign: "center",
            fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2rem" },
          }}
        >
          🔁 Promoción 2×1
        </Typography>

        {!isMobile && (
          <>
            <NavButton ref={prevRef} sx={{ left: -18 }}>
              <ChevronLeftIcon fontSize="medium" />
            </NavButton>
            <NavButton ref={nextRef} sx={{ right: -18 }}>
              <ChevronRightIcon fontSize="medium" />
            </NavButton>
          </>
        )}

        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          onInit={(swiper) => {
            if (!isMobile) {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
              swiper.navigation.init();
              swiper.navigation.update();
            }
          }}
          loop
          allowTouchMove={isMobile}
          preventClicks={false}
          preventClicksPropagation={false}
          touchStartPreventDefault={false}
          centeredSlides={!isMobile}
          centeredSlidesBounds={!isMobile}
          spaceBetween={12}
          slidesPerView={1}
          autoplay={
            isMobile
              ? {
                  delay: 3000,
                  disableOnInteraction: false,
                }
              : false
          }
          pagination={{
            el: ".twoforone-swiper-pagination",
            clickable: true,
            bulletClass: "swiper-pagination-bullet twoforone-bullet",
            bulletActiveClass: "swiper-pagination-bullet-active twoforone-bullet-active",
          }}
          breakpoints={{
            600: { slidesPerView: 2, spaceBetween: 14 },
            900: { slidesPerView: 3, spaceBetween: 16 },
            1200: { slidesPerView: 4, spaceBetween: 16 },
          }}
        >
          {items.map((product) => (
            <SwiperSlide key={product.id}>
              <Box sx={{ px: { xs: 0.5, sm: 1 } }}>
                <ProductCard product={product} />
              </Box>
            </SwiperSlide>
          ))}
        </Swiper>

        <Box
          className="twoforone-swiper-pagination"
          sx={{
            display: { xs: "flex", sm: "none" },
            justifyContent: "center",
            gap: 1,
            mt: 1.5,
            "& .twoforone-bullet": {
              width: 8,
              height: 8,
              background: "#e1bee7",
              opacity: 1,
              borderRadius: "50%",
            },
            "& .twoforone-bullet-active": {
              background: "#7E57C2",
            },
          }}
        />
      </Box>
    </motion.div>
  );
}
